import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import "./css/portfolio.css";

// Portfolio categories shown in the header switcher. Adding a category here
// also requires a route in App.js and a wrapper component below.
const CATEGORIES = [
    { type: "Residential", path: "/residential" },
    { type: "Commercial", path: "/commercial" },
    { type: "ADU", path: "/adu" },
];
import PortfolioItem from './PortfolioItem';
import Seo from './Seo';

function Portfolio({ type, apiEndpoint, statsFields, itemFields }) {
    const [record, setRecord] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [animatedValues, setAnimatedValues] = useState(
        Object.fromEntries(statsFields.map(field => [field.key, 0]))
    );

    useEffect(() => {
        fetchRecord();
        fetchCustomers();
    }, []);

    function animateValue(key, end, duration) {
        if (isNaN(end)) return;
        let start = animatedValues[key];
        let range = end - start;
        let minTimer = 50;
        let stepTime = Math.abs(Math.floor(duration / range));
        stepTime = Math.max(stepTime, minTimer);
        let startTime = new Date().getTime();
        let endTime = startTime + duration;
      
        let timer = setInterval(() => {
          let now = new Date().getTime();
          let remaining = Math.max((endTime - now) / duration, 0);
          let value = Math.round(end - (remaining * range));
          
          setAnimatedValues(prev => ({ ...prev, [key]: value }));
      
          if (value === end) {
            clearInterval(timer);
          }
        }, stepTime);
    }

    async function fetchRecord() {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/record/sum/${apiEndpoint}`);
            if (!response.ok) {
                console.error(`An error has occurred: ${response.statusText}`);
                return;
            }
            const record = await response.json();
            setRecord(record);
            if (record) {
                statsFields.forEach(field => {
                    animateValue(field.key, record[field.key], 1500);
                });
            }
        } catch (error) {
            console.error(error);
        }
    }

    async function fetchCustomers() {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/record/special-customers/${apiEndpoint}`);
            if (!response.ok) {
                console.error(`An error has occurred: ${response.statusText}`);
                return;
            }
            let customers = await response.json();
            customers = customers.map(customer => ({
                ...customer,
                imageUrl: customer.image || sun
            }));
            setCustomers(customers);
        } catch (error) {
            console.error(error);
        }
    }

    return (
        <div className="portfolio">
            <Seo
                title={`${type} Solar Installations in San Diego | PV Solar`}
                description={`Browse PV Solar's ${type.toLowerCase()} solar and battery installations across San Diego County — real systems with panel counts, sizes, and lifetime energy production.`}
                path={`/${apiEndpoint}`}
            />
            <div className="portfolio-header">
                <p className="portfolio-eyebrow">Our Portfolio</p>
                <h1 className="title portfolio-switch">
                    {CATEGORIES.map((cat, i) => (
                        <React.Fragment key={cat.type}>
                            {i > 0 && <span className="switch-divider">|</span>}
                            <Link to={cat.path} className={type === cat.type ? "active" : ""}>
                                {cat.type}
                            </Link>
                        </React.Fragment>
                    ))}
                </h1>
                <p className="subtitle">
                    {statsFields
                        .filter(field => record && record[field.key] > 0)
                        .map((field, index, visible) => (
                            <span key={field.key}>
                                {animatedValues[field.key].toLocaleString()} {field.label}
                                {index < visible.length - 1 && " | "}
                            </span>
                        ))}
                </p>
            </div>
            <div className="portfolio">
                {customers.map((customer, index) => (
                    <PortfolioItem
                        key={index}
                        src={customer.imageUrl}
                        alt={`Image ${index + 1}`}
                        city={`${customer.address.city}, ${customer.address.state}`}
                        {...Object.fromEntries(
                            itemFields.map(field => [
                                field.key,
                                { value: customer[field.valueKey] / (field.divisor || 1), text: field.label }
                            ])
                        )}
                    />
                ))}
            </div>
        </div>
    );
}

// Residential Portfolio
export function ResidentialPortfolio() {
    return (
        <Portfolio
            type="Residential"
            apiEndpoint="residential"
            statsFields={[
                { key: "totalPanels", label: "panels" },
                { key: "totalWatts", label: "kW AC" },
                { key: "totalKva", label: "kVA of battery power" },
                { key: "totalWattHours", label: "kWh of energy storage" },
                { key: "totalEnergy", label: "MWh generated" }
            ]}
            itemFields={[
                { key: "anotherText", valueKey: "size_w", label: "kW", divisor: 1000 },
                { key: "description", valueKey: "modules", label: "Panels" },
                { key: "battery", valueKey: "battery_capacity_wh", label: "kWh", divisor: 1000 }
            ]}
        />
    );
}

// Commercial Portfolio
export function CommercialPortfolio() {
    return (
        <Portfolio
            type="Commercial"
            apiEndpoint="commercial"
            statsFields={[
                { key: "totalPanels", label: "panels" },
                { key: "totalWatts", label: "kW AC" },
                { key: "totalEnergy", label: "MWh generated" }
            ]}
            itemFields={[
                { key: "anotherText", valueKey: "size_w", label: "Watts" },
                { key: "description", valueKey: "modules", label: "Panels" }
            ]}
        />
    );
}

// ADU Portfolio — populated by tagging customers with type "adu" in the
// uploader; renders empty (zero stats, no cards) until then.
export function AduPortfolio() {
    return (
        <Portfolio
            type="ADU"
            apiEndpoint="adu"
            statsFields={[
                { key: "totalPanels", label: "panels" },
                { key: "totalWatts", label: "kW AC" },
                { key: "totalWattHours", label: "kWh of energy storage" },
                { key: "totalEnergy", label: "MWh generated" }
            ]}
            itemFields={[
                { key: "anotherText", valueKey: "size_w", label: "kW", divisor: 1000 },
                { key: "description", valueKey: "modules", label: "Panels" },
                { key: "battery", valueKey: "battery_capacity_wh", label: "kWh", divisor: 1000 }
            ]}
        />
    );
}